/**
 * DataGrid Adapter for AuditLogs
 * Transforms field registry to MUI DataGrid column definitions
 */

import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { BaseFieldDefinition } from '../../../shared/types/fieldRegistry';
import { createCellRenderer as createSharedCellRenderer } from '../../../shared/utils/cellRenderers';
import { getGridFilterOperatorsForType } from '../../../utils/fieldOperators';
import { AUDIT_LOGS_FIELDS_REGISTRY } from '../fields';
import { AuditLog } from '../../../services/api/domains/audit/types';

// Helper function to get operation type badge styles
const getOperationTypeStyle = (
  operationType: AuditLog['operation_type']
): React.CSSProperties => {
  const baseStyle: React.CSSProperties = {
    padding: '2px 6px',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: '500',
  };

  switch (operationType) {
    case 'INSERT':
      return {
        ...baseStyle,
        backgroundColor: '#dcfce7',
        color: '#166534',
      };
    case 'UPDATE':
      return {
        ...baseStyle,
        backgroundColor: '#dbeafe',
        color: '#1e40af',
      };
    case 'DELETE':
      return {
        ...baseStyle,
        backgroundColor: '#fee2e2',
        color: '#991b1b',
      };
    case 'MERGE':
      return {
        ...baseStyle,
        backgroundColor: '#fef3c7',
        color: '#92400e',
      };
    default:
      return baseStyle;
  }
};

/**
 * Create a cell renderer for a specific field
 * Uses shared renderer with custom overrides for audit-specific fields
 */
function createAuditLogCellRenderer(
  field: BaseFieldDefinition<AuditLog>
): GridColDef['renderCell'] {
  // Custom rendering for operation_type (badge)
  if (field.key === 'operation_type') {
    return (params: GridRenderCellParams<AuditLog>) => (
      <span style={getOperationTypeStyle(params.row.operation_type)}>
        {params.row.operation_type}
      </span>
    );
  }

  // Custom rendering for field_name (show dash if null)
  if (field.key === 'field_name') {
    return (params: GridRenderCellParams<AuditLog>) =>
      params.row.field_name || '—';
  }

  // Custom rendering for old_value and new_value (JSON formatting)
  if (field.key === 'old_value' || field.key === 'new_value') {
    return (params: GridRenderCellParams<AuditLog>) => {
      const value =
        field.key === 'old_value' ? params.row.old_value : params.row.new_value;

      if (!value) {
        return <span style={{ color: '#9ca3af' }}>—</span>;
      }

      // Try to parse as JSON for pretty printing
      try {
        const parsed = JSON.parse(value);
        return (
          <span
            style={{
              fontFamily: 'monospace',
              fontSize: '0.85rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}>
            {JSON.stringify(parsed)}
          </span>
        );
      } catch {
        // Not JSON, render as-is
        return (
          <span
            style={{
              fontFamily: 'monospace',
              fontSize: '0.85rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}>
            {value}
          </span>
        );
      }
    };
  }

  // Custom rendering for change_reason (show dash if null)
  if (field.key === 'change_reason') {
    return (params: GridRenderCellParams<AuditLog>) =>
      params.row.change_reason || '—';
  }

  // Use shared renderer for all other fields
  return createSharedCellRenderer<AuditLog>(field);
}

/**
 * Transform field registry to DataGrid column definitions
 */
export function toDataGridColumns(): GridColDef<AuditLog>[] {
  return AUDIT_LOGS_FIELDS_REGISTRY.filter(
    // Exclude audit_id from display by default
    field => field.key !== 'audit_id'
  ).map(field => {
    const column: GridColDef<AuditLog> = {
      field: field.key,
      headerName: field.label,
      sortable: field.isSortable,
      filterable: field.isFilterable,
    };

    // Apply render configuration
    if (field.renderConfig) {
      if (field.renderConfig.width) column.width = field.renderConfig.width;
      if (field.renderConfig.minWidth)
        column.minWidth = field.renderConfig.minWidth;
      if (field.renderConfig.flex) column.flex = field.renderConfig.flex;
    }

    // Apply filter operators with type casting (though filterable is false for all audit fields)
    if (field.isFilterable) {
      // Check for selection fields first
      if (field.selectionOptions && field.selectionOptions.length > 0) {
        column.filterOperators = getGridFilterOperatorsForType(
          'selection',
          field.selectionOptions
        );
      } else {
        column.filterOperators = getGridFilterOperatorsForType(
          field.dataType as 'string' | 'number' | 'date'
        );
      }
    }

    // Apply custom cell renderer
    column.renderCell = createAuditLogCellRenderer(field);

    return column;
  });
}

/**
 * Get default column visibility model
 * Hides change_reason by default
 */
export function getDefaultColumnVisibility(): Record<string, boolean> {
  return {
    change_reason: false,
  };
}
